import { FC, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import { DeleteIcon } from '../../../team/components/CreateMember/DeleteIcon';
import { TBonus } from '../../../../types';

interface BonusesInputProps {
  bonuses: TBonus[];
  onChange: (bonuses: TBonus[]) => void;
  maxSteps?: number;
  currencyMark?: string;
  isChecked: boolean
}

const BonusesInput: FC<BonusesInputProps> = ({
  bonuses,
  onChange,
  maxSteps = 3,
  currencyMark = '$',
  isChecked,
}) => {
  const { t } = useTranslation(['commissions', 'inputs']);
  const [localBonuses, setLocalBonuses] = useState(bonuses);

  const handleBonusChange = (index: number, key: keyof TBonus, value: string | number | boolean) => {
    const updatedBonuses = localBonuses?.map((bonus, idx) => {
      if (idx === index) {
        return { ...bonus, [key]: value };
      }
      return bonus;
    });

    setLocalBonuses(updatedBonuses);
    onChange(updatedBonuses);
  };

  const addBonus = () => {
    if (localBonuses.length < maxSteps) {
      const newBonus: TBonus = {
        index: localBonuses.length,
        name: '',
        from: 0,
        fixedType: 'flat',
        fixedAmount: 0,
      };
      const updatedBonuses = [...localBonuses, newBonus];
      setLocalBonuses(updatedBonuses);
      onChange(updatedBonuses);
    }
  };

  const deleteBonus = (index: number) => {
    const updatedBonuses = localBonuses.filter((_, idx) => idx !== index);
    setLocalBonuses(updatedBonuses);
    onChange(updatedBonuses);
  };

  const handleTypeChange = (index: number, value: 'flat' | 'percent') => {
    const updatedBonuses = localBonuses.map((bonus, idx) => {
      if (idx === index) {
        return {
          ...bonus,
          fixedType: value,
          fixedAmount: 0,
        };
      }
      return bonus;
    });

    setLocalBonuses(updatedBonuses);
    onChange(updatedBonuses);
  };

  return (
    <Grid container spacing={2}>
      {localBonuses?.map((bonus, index) => (
        <Grid container key={index?.toString()} spacing={2} alignItems="center">
          <Grid size={{
            xs: 6, sm: 3, md: 3,
          }}
          >
            <TextField
              disabled={!isChecked}
              label={t('name', { ns: 'inputs' })}
              value={bonus.name}
              size="small"
              onChange={(e) => handleBonusChange(index, 'name', e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid size={{
            xs: 6, sm: 2, md: 2,
          }}
          >
            <NumericFormat
              size="small"
              disabled={!isChecked}
              customInput={TextField}
              label={t('goal')}
              value={bonus.from}
              onValueChange={(values) => handleBonusChange(index, 'from', Number(values.value))}
              fullWidth
            />
          </Grid>
          <Grid
            display="flex"
            justifyContent="center"
            size={{
              xs: 6, md: 4, sm: 4,
            }}
          >
            <RadioGroup
              row
              value={bonus.fixedType}
              onChange={(e) => handleTypeChange(index, e.target.value as 'flat' | 'percent')}
            >
              <FormControlLabel value="flat" control={<Radio disabled={!isChecked} />} label={t('flat')} />
              <FormControlLabel value="percent" control={<Radio disabled={!isChecked} />} label={t('percentage')} />
            </RadioGroup>
          </Grid>
          <Grid size={{ xs: 5, sm: 2, md: 2 }}>
            <NumericFormat
              customInput={TextField}
              disabled={!isChecked}
              size="small"
              label={t('compensation')}
              value={bonus.fixedAmount}
              onValueChange={(values) => handleBonusChange(
                index,
                'fixedAmount',
                Number(values.value),
              )}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2">
                      {bonus.fixedType === 'flat' ? currencyMark : '%'}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 1, md: 1 }}>
            {localBonuses?.length > 1 && (
              <DeleteIcon size={0.8} onClick={() => deleteBonus(index)} />
            )}
          </Grid>
        </Grid>
      ))}
      {localBonuses?.length < maxSteps && (
        <Box mt={2}>
          <Button
            disabled={!isChecked}
            variant="outlined"
            onClick={addBonus}
          >
            {t('addBonus')}
          </Button>
        </Box>
      )}
    </Grid>
  );
};

export { BonusesInput };
